import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { Timestamp } from 'firebase/firestore';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
    OrgDraft,
    OrgDraftPart,
    OrgDraftSnapshotPayload,
    OrgDraftSourceMode,
    OrgTemplate,
    OrgTemplateScope,
    ScaffoldVariant,
    StaffRow,
} from '@newgenus/common';
import { OrgWizardStateService } from '../services/org-wizard-state.service';
import {
    ImportIssue,
    ImportedDraftPayload,
    OrgExcelImportService,
} from '../../org-board/shared/excel/org-excel-import.service';
import { OrgTemplateService } from '../services/org-template.service';
import { OrgSourcePickerDialogComponent, OrgSourcePickerResult } from '../dialogs/org-source-picker-dialog.component';

/**
 * Step 1 — Source.
 *
 * Five cards per D15: Blank · Rudimentary scaffold (recommended) ·
 * Curated template · Clone from an org I admin · Import from Excel.
 *
 * P1 wires Blank and Scaffold. Template / Clone are still P3+ scope and remain
 * disabled. P3 enables "Import from Excel" — see {@link OrgExcelImportService}
 * and Plan Addendum 16. The importer hydrates `state.structure` + `state.staff`
 * from the workbook and routes the user to Step 2 so the full wizard review
 * flow runs as normal (no short-circuiting); Step 4's debounced `checkEmail`
 * surfaces existing-user / will-be-invited / invalid badges per row.
 *
 * Selecting `scaffold` seeds the draft's `structure` array with a CEO + Division
 * + Department triple, so the user has a non-empty starting tree.
 */
@Component({
    selector: 'newgenus-org-creation-step-source',
    templateUrl: './step-1-source.component.html',
    styleUrls: ['./step-1-source.component.scss'],
    standalone: false,
})
export class StepSourceComponent {
    public state = inject(OrgWizardStateService);
    private excelImport = inject(OrgExcelImportService);
    private templates = inject(OrgTemplateService);
    private toastr = inject(ToastrService);
    private dialog = inject(MatDialog);

    @ViewChild('excelInput') private excelInput?: ElementRef<HTMLInputElement>;

    /** True while the importer is parsing — disables the card to prevent double-clicks. */
    public isImporting = false;
    /** True while a template / clone source is loading — disables both picker cards. */
    public isPickingSource = false;

    public choose(mode: OrgDraftSourceMode, scaffoldVariant?: ScaffoldVariant): void {
        const cur = this.state.snapshot;
        if (!cur) return;

        const patch: Partial<OrgDraft> = {
            sourceMode: mode,
            sourceRef: scaffoldVariant ? { scaffoldVariant } : {},
            structure: this.seedStructure(mode, scaffoldVariant),
        };
        this.state.dispatch(patch);
    }

    private seedStructure(mode: OrgDraftSourceMode, variant?: ScaffoldVariant): OrgDraftPart[] {
        if (mode !== 'scaffold') return [];

        // Minimal scaffold matching the canonical org-board pattern:
        //   • root  = Executive CONTAINER  ("Chief Executive Officer")
        //   • inside it: CEO POST as headerRole='head' + Division CONTAINER
        //     • Division contains a Department CONTAINER
        // The CEO post is the staffable position (a hat is auto-created for it),
        // but it lives INSIDE the executive container — not at the root, where
        // a bare post can't host child containers.
        // Keys are client-generated and survive into the backend `bulkAddParts` command.
        const execKey = this.keyFor('exec');
        const ceoKey = this.keyFor('ceo');
        const divKey = this.keyFor('div');
        const deptKey = this.keyFor('dept');

        const base: OrgDraftPart[] = [
            {
                partKey: execKey,
                name: 'Chief Executive Officer',
                partType: 'executive',
                parentPartKey: '',
                id: 0,
                depth: 0,
            },
            {
                partKey: ceoKey,
                name: 'CEO',
                partType: 'post',
                parentPartKey: execKey,
                id: 0,
                depth: 1,
                headerRole: 'head',
            },
            {
                partKey: divKey,
                name: 'Division',
                partType: 'division',
                parentPartKey: execKey,
                id: 1,
                depth: 1,
            },
            {
                partKey: deptKey,
                name: 'Department',
                partType: 'department',
                parentPartKey: divKey,
                id: 0,
                depth: 2,
            },
        ];

        if (variant === 'with-executive-team') {
            // Add a placeholder Executive Assistant post in the executive's header strip,
            // alongside the CEO head post.
            base.push({
                partKey: this.keyFor('exec-asst'),
                name: 'Executive Assistant',
                partType: 'post',
                parentPartKey: execKey,
                id: 2,
                depth: 1,
                headerRole: 'assistant',
            });
        }

        return base;
    }

    private keyFor(label: string): string {
        // Short, client-side temporary key. Backend may rewrite to a Firestore ID
        // on submit, but for the draft the key only needs to be unique within the tree.
        return `draft-${label}-${Math.random().toString(36).slice(2, 8)}`;
    }

    public isSelected(mode: OrgDraftSourceMode, variant?: ScaffoldVariant): boolean {
        const cur = this.state.snapshot;
        if (!cur) return false;
        if (cur.sourceMode !== mode) return false;
        if (mode === 'scaffold') return cur.sourceRef?.scaffoldVariant === variant;
        return true;
    }

    // ── Excel import (P3) ───────────────────────────────────────────────

    /** Trigger the hidden `<input type="file">` from the card click. */
    public openExcelPicker(): void {
        if (this.isImporting) return;
        this.excelInput?.nativeElement.click();
    }

    /** Wired to `(change)` on the hidden file input. */
    public async onExcelFileChosen(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        // Reset the input value so re-picking the same file fires the change event again.
        input.value = '';
        if (!file) return;

        const cur = this.state.snapshot;
        if (!cur) return;

        this.isImporting = true;
        try {
            const result = await this.excelImport.parseFile(file);
            if (!result.ok || !result.data) {
                this.showImportFailure(file.name, result.issues);
                return;
            }
            this.applyImportedDraft(file.name, result.data, result.issues);
        } catch (err) {
            console.error('[Step1] Excel import failed:', err);
            this.toastr.error(
                `Could not parse ${file.name}: ${(err as Error).message ?? 'unknown error'}`,
                'Import from Excel'
            );
        } finally {
            this.isImporting = false;
        }
    }

    private applyImportedDraft(filename: string, payload: ImportedDraftPayload, issues: ImportIssue[]): void {
        // Patch source metadata — basics.name pre-populated from the source org name
        // so Step 2 has a sensible default the user can edit.
        const cur = this.state.snapshot;
        if (!cur) return;

        this.state.dispatch({
            sourceMode: 'excel',
            sourceRef: { excelImportedAt: Timestamp.now() },
            basics: {
                ...cur.basics,
                name: cur.basics?.name?.trim() ? cur.basics.name : payload.sourceOrgName,
            },
            staff: [],
        });

        // Structure: setStructure handles its own history entry.
        this.state.setStructure(payload.parts);

        // Staff: feed each imported row through the existing addStaffRow API so
        // Step 4's `checkEmail` resolver picks them up on first visit.
        for (const row of payload.staff) {
            this.state.addStaffRow(row as Partial<StaffRow>);
        }

        // Land on Step 2 so the user reviews basics → structure → features → staff → review.
        this.state.setStep(2);

        const warnCount = issues.filter((i) => i.severity === 'warning').length;
        const summary =
            `Imported ${payload.parts.length} part(s), ${payload.staff.length} staff row(s)` +
            (warnCount > 0 ? ` with ${warnCount} warning(s) — see browser console` : '') +
            ` from ${filename}.`;
        if (warnCount > 0) {
            console.warn(
                '[Step1] Excel import warnings:',
                issues.filter((i) => i.severity === 'warning')
            );
            this.toastr.warning(summary, 'Import from Excel');
        } else {
            this.toastr.success(summary, 'Import from Excel');
        }
    }

    private showImportFailure(filename: string, issues: ImportIssue[]): void {
        const errors = issues.filter((i) => i.severity === 'error');
        console.error('[Step1] Excel import rejected:', errors);
        const head = errors[0];
        const headMsg = head ? `${head.sheet}${head.row ? ' row ' + head.row : ''}: ${head.message}` : 'Unknown error.';
        const tail = errors.length > 1 ? ` (+${errors.length - 1} more — see browser console)` : '';
        this.toastr.error(`${filename}: ${headMsg}${tail}`, 'Import from Excel');
    }

    // ── Curated template + Clone-from-my-org (P4.a + P4.b) ──────────────

    /** Step 1 "Curated template" card click. Opens the picker, then applies the chosen template. */
    public openTemplatePicker(): void {
        if (this.isPickingSource) return;
        this.openSourcePicker('template');
    }

    /** Step 1 "Clone an org I admin" card click. Opens the picker, then clones the chosen org. */
    public openClonePicker(): void {
        if (this.isPickingSource) return;
        this.openSourcePicker('clone');
    }

    private openSourcePicker(mode: 'template' | 'clone'): void {
        const ref = this.dialog.open<
            OrgSourcePickerDialogComponent,
            { mode: 'template' | 'clone' },
            OrgSourcePickerResult
        >(OrgSourcePickerDialogComponent, { data: { mode }, panelClass: 'newgenus-source-picker-panel' });
        ref.afterClosed().subscribe(async (result) => {
            if (!result || result.kind === 'cancel') return;
            this.isPickingSource = true;
            try {
                if (result.kind === 'template') {
                    this.applyTemplate(result.template);
                } else {
                    await this.applyClone(result.org.orgKey, result.org.name);
                }
            } catch (err) {
                console.error('[Step1] Source apply failed:', err);
                this.toastr.error(
                    (err as Error).message || 'Failed to apply source.',
                    mode === 'template' ? 'Curated template' : 'Clone org'
                );
            } finally {
                this.isPickingSource = false;
            }
        });
    }

    private applyTemplate(template: OrgTemplate): void {
        const cur = this.state.snapshot;
        if (!cur) return;
        const parts = Array.isArray(template.snapshot?.parts) ? template.snapshot.parts : [];

        // v2: thread the snapshot's hats / SGs / policies onto the draft so
        // the orchestrator's apply stages can write them on submit. v1
        // templates have no `snapshotVersion` and these branches no-op.
        const snapshotPayload = this.snapshotPayloadFromTemplate(template);

        const patch: Partial<OrgDraft> = {
            sourceMode: 'template',
            sourceRef: { templateKey: template.key },
            basics: {
                ...cur.basics,
                name: cur.basics?.name?.trim() ? cur.basics.name : template.name,
            },
            staff: [],
        };
        if (snapshotPayload) patch.snapshotPayload = snapshotPayload;

        this.state.dispatch(patch);
        this.state.setStructure(parts);
        this.state.setStep(2);

        const extras: string[] = [];
        if (snapshotPayload?.hats) extras.push(`${snapshotPayload.hats.length} hat(s)`);
        if (snapshotPayload?.securityGroups) extras.push(`${snapshotPayload.securityGroups.length} SG(s)`);
        if (snapshotPayload?.policies) extras.push(`${snapshotPayload.policies.length} policy/ies`);
        const extrasText = extras.length ? ` (+ ${extras.join(', ')})` : '';
        this.toastr.success(`Loaded "${template.name}" — ${parts.length} part(s)${extrasText}.`, 'Curated template');
    }

    /** Build a `OrgDraftSnapshotPayload` from a v2 template doc, or `undefined` for v1 templates. */
    private snapshotPayloadFromTemplate(template: OrgTemplate): OrgDraftSnapshotPayload | undefined {
        if ((template.snapshotVersion ?? 1) < 2) return undefined;
        const s = template.snapshot;
        const hasV2Data = !!(s.hats?.length || s.securityGroups?.length || s.policies?.length);
        if (!hasV2Data) return undefined;
        const scope: OrgTemplateScope[] =
            Array.isArray(template.snapshotScope) && template.snapshotScope.length
                ? [...template.snapshotScope]
                : ['structure', 'features', 'settings'];
        const payload: OrgDraftSnapshotPayload = { snapshotVersion: 2, scope };
        if (s.hats?.length) payload.hats = s.hats;
        if (s.securityGroups?.length) payload.securityGroups = s.securityGroups;
        if (s.policies?.length) payload.policies = s.policies;
        return payload;
    }

    private async applyClone(orgKey: string, orgName: string): Promise<void> {
        const { parts, snapshotPayload } = await this.templates.loadOrgFullSnapshot(orgKey);
        if (parts.length === 0) {
            this.toastr.warning(`"${orgName}" has no parts to clone.`, 'Clone org');
            return;
        }
        const cur = this.state.snapshot;
        if (!cur) return;

        const patch: Partial<OrgDraft> = {
            sourceMode: 'clone',
            sourceRef: { clonedFromOrgKey: orgKey },
            basics: {
                ...cur.basics,
                name: cur.basics?.name?.trim() ? cur.basics.name : `${orgName} (clone)`,
            },
            staff: [],
        };
        if (snapshotPayload) patch.snapshotPayload = snapshotPayload;

        this.state.dispatch(patch);
        this.state.setStructure(parts);
        this.state.setStep(2);

        const extras: string[] = [];
        if (snapshotPayload?.hats) extras.push(`${snapshotPayload.hats.length} hat(s)`);
        if (snapshotPayload?.securityGroups) extras.push(`${snapshotPayload.securityGroups.length} SG(s)`);
        if (snapshotPayload?.policies) extras.push(`${snapshotPayload.policies.length} policy/ies`);
        const extrasText = extras.length ? ` (+ ${extras.join(', ')})` : '';
        this.toastr.success(`Cloned "${orgName}" — ${parts.length} part(s)${extrasText}.`, 'Clone org');
    }
}
